import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface LeaderboardEntry {
  user_id: string;
  username: string;
  name: string | null;
  image: string | null;
  image_thumb: string | null;
  level: number;
  level_title: string | null;
  rank: number;
}

interface LeaderboardCardProps {
  entry: LeaderboardEntry;
  statLabel: string;
  statValue: string;
  isCurrentUser: boolean;
  compact?: boolean;
}

export function LeaderboardCard({ entry, statLabel, statValue, isCurrentUser, compact = false }: LeaderboardCardProps) {
  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return "bg-yellow-500 text-white";
    if (rank === 2) return "bg-gray-400 text-white";
    if (rank === 3) return "bg-amber-600 text-white";
    return "bg-muted";
  };

  if (compact) {
    return (
      <Card className={`p-3 ${isCurrentUser ? 'ring-2 ring-primary' : ''}`}>
        <Link to={`/u/${entry.username}`} className="flex flex-col items-center gap-2">
          <Avatar className="h-12 w-12">
            <AvatarImage src={entry.image_thumb || entry.image || undefined} />
            <AvatarFallback>{entry.username[0].toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="text-center">
            <div className="font-semibold text-sm truncate max-w-[100px]">
              {entry.name || entry.username}
            </div>
            <div className="text-xs text-muted-foreground">
              {statValue} {statLabel}
            </div>
          </div>
        </Link>
      </Card>
    );
  }

  return (
    <Card className={`p-4 transition-all hover:shadow-md ${isCurrentUser ? 'ring-2 ring-primary' : ''}`}>
      <Link to={`/u/${entry.username}`} className="flex items-center gap-4">
        <Badge className={`${getRankBadgeColor(entry.rank)} shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold`}>
          #{entry.rank}
        </Badge>

        <Avatar className="h-12 w-12">
          <AvatarImage src={entry.image_thumb || entry.image || undefined} />
          <AvatarFallback>{entry.username[0].toUpperCase()}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">
            {entry.name || entry.username}
            {isCurrentUser && <span className="text-primary ml-2">(You)</span>}
          </div>
          <div className="text-sm text-muted-foreground">
            Level {entry.level} {entry.level_title && `• ${entry.level_title}`}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="text-2xl font-bold text-primary">{statValue}</div>
          <div className="text-xs text-muted-foreground">{statLabel}</div>
        </div>
      </Link>
    </Card>
  );
}
